import {
    addDoc,
    collection,
    deleteDoc,
    deleteField,
    doc,
    onSnapshot,
    orderBy,
    query,
    updateDoc,
    where,
    limit,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import type { FastingSession } from '../types';

export function toLocalDateKey(iso: string): string {
    const d = new Date(iso);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

/** Overnight fasts are attributed to the calendar day the fast started. */
export function resolveSessionStartDate(session: FastingSession): string {
    return session.startDate || toLocalDateKey(session.startedAt);
}

export function buildRecentDayKeys(count: number, now: Date = new Date()): string[] {
    const keys: string[] = [];
    const cursor = new Date(now);
    cursor.setHours(0, 0, 0, 0);
    for (let i = count - 1; i >= 0; i -= 1) {
        const day = new Date(cursor);
        day.setDate(cursor.getDate() - i);
        const y = day.getFullYear();
        const m = String(day.getMonth() + 1).padStart(2, '0');
        const d = String(day.getDate()).padStart(2, '0');
        keys.push(`${y}-${m}-${d}`);
    }
    return keys;
}

const HEBREW_WEEKDAY_LETTERS = ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ש'] as const;

export function formatFastingWeekdayLabel(dateKey: string, language: string): string {
    const [y, m, d] = dateKey.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    if (language === 'he') {
        return HEBREW_WEEKDAY_LETTERS[date.getDay()];
    }
    return date.toLocaleDateString(language, { weekday: 'short' });
}

function sessionsCollection(userId: string) {
    return collection(db, 'users', userId, 'fastingSessions');
}

export function subscribeActiveFastingSession(
    userId: string,
    onSession: (session: FastingSession | null) => void,
): () => void {
    const q = query(
        sessionsCollection(userId),
        where('status', '==', 'active'),
        limit(1),
    );
    return onSnapshot(q, (snap) => {
        if (snap.empty) {
            onSession(null);
            return;
        }
        const d = snap.docs[0];
        onSession({ ...(d.data() as Omit<FastingSession, 'id'>), id: d.id });
    });
}

export function subscribeCompletedFastingSessions(
    userId: string,
    onSessions: (sessions: FastingSession[]) => void,
): () => void {
    const q = query(
        sessionsCollection(userId),
        orderBy('startedAt', 'desc'),
        limit(200),
    );
    return onSnapshot(
        q,
        (snap) => {
            onSessions(snap.docs.map((d) => ({ ...(d.data() as Omit<FastingSession, 'id'>), id: d.id })));
        },
        (error) => {
            console.error('Error fetching completed fasting sessions:', error);
        }
    );
}

export function subscribeScheduledFastingSession(
    userId: string,
    onSession: (session: FastingSession | null) => void,
): () => void {
    const q = query(
        sessionsCollection(userId),
        where('status', '==', 'scheduled'),
        limit(1),
    );
    return onSnapshot(q, (snap) => {
        if (snap.empty) {
            onSession(null);
            return;
        }
        const d = snap.docs[0];
        onSession({ ...(d.data() as Omit<FastingSession, 'id'>), id: d.id });
    });
}

export async function startFastingSession(userId: string, goalHours: number, startedAt?: string): Promise<void> {
    const now = startedAt ?? new Date().toISOString();
    await addDoc(sessionsCollection(userId), {
        startedAt: now,
        startDate: toLocalDateKey(now),
        status: 'active',
        goalHours,
    } satisfies Omit<FastingSession, 'id'>);
}

export async function scheduleFastingSession(
    userId: string,
    goalHours: number,
    scheduledStartAt: string,
): Promise<void> {
    await addDoc(sessionsCollection(userId), {
        scheduledStartAt,
        startedAt: scheduledStartAt,
        startDate: toLocalDateKey(scheduledStartAt),
        status: 'scheduled',
        goalHours,
    } satisfies Omit<FastingSession, 'id'>);
}

export async function activateScheduledFastingSession(userId: string, session: FastingSession): Promise<void> {
    const startedAt = session.scheduledStartAt ?? session.startedAt;
    await updateDoc(doc(db, 'users', userId, 'fastingSessions', session.id), {
        status: 'active',
        startedAt,
        startDate: toLocalDateKey(startedAt),
        scheduledStartAt: deleteField(),
    });
}

export async function cancelScheduledFastingSession(userId: string, sessionId: string): Promise<void> {
    await deleteDoc(doc(db, 'users', userId, 'fastingSessions', sessionId));
}

export async function updateFastingSessionStartTime(
    userId: string,
    sessionId: string,
    newStartedAt: string,
): Promise<void> {
    await updateDoc(doc(db, 'users', userId, 'fastingSessions', sessionId), {
        startedAt: newStartedAt,
        startDate: toLocalDateKey(newStartedAt),
    });
}

export async function stopFastingSession(userId: string, session: FastingSession): Promise<void> {
    const endedAt = new Date().toISOString();
    const durationMs = new Date(endedAt).getTime() - new Date(session.startedAt).getTime();
    const durationMinutes = Math.max(1, Math.round(durationMs / 60000));
    await updateDoc(doc(db, 'users', userId, 'fastingSessions', session.id), {
        endedAt,
        durationMinutes,
        status: 'completed',
        startDate: resolveSessionStartDate(session),
    });
}

export function getElapsedMinutes(session: FastingSession, nowMs: number = Date.now()): number {
    return Math.max(0, Math.floor((nowMs - new Date(session.startedAt).getTime()) / 60000));
}

export function formatFastingDuration(totalMinutes: number): { hours: number; minutes: number } {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return { hours, minutes };
}

export const FASTING_PERIOD_COLORS = [
    '#0ea5e9',
    '#8b5cf6',
    '#f59e0b',
    '#10b981',
    '#f43f5e',
    '#6366f1',
] as const;

export interface FastingDaySummary {
    date: string;
    sessions: FastingSession[];
    totalMinutes: number;
}

export interface FastingChartSession {
    id: string;
    durationMinutes: number;
    colorIndex: number;
}

export interface FastingChartDay {
    date: string;
    sessions: FastingChartSession[];
    totalMinutes: number;
}

export function groupFastingSessionsByDate(sessions: FastingSession[], nowMs: number = Date.now()): FastingDaySummary[] {
    const byDate = new Map<string, FastingSession[]>();
    sessions
        .filter((session) => session.status === 'completed' || session.status === 'active')
        .forEach((session) => {
            const date = resolveSessionStartDate(session);
            const list = byDate.get(date) ?? [];
            list.push(session);
            byDate.set(date, list);
        });

    return Array.from(byDate.entries())
        .map(([date, daySessions]) => {
            const sorted = [...daySessions].sort(
                (a, b) => new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime(),
            );
            const totalMinutes = sorted.reduce((sum, s) => {
                const duration = s.status === 'active' ? getElapsedMinutes(s, nowMs) : (s.durationMinutes ?? 0);
                return sum + duration;
            }, 0);
            return { date, sessions: sorted, totalMinutes };
        })
        .sort((a, b) => b.date.localeCompare(a.date));
}

export function buildFastingChartDays(
    sessions: FastingSession[],
    dayCount: number = 14,
    now: Date = new Date(),
): FastingChartDay[] {
    const nowMs = now.getTime();
    const byDate = new Map<string, FastingSession[]>();
    sessions
        .filter((session) => session.status === 'completed' || session.status === 'active')
        .forEach((session) => {
            const date = resolveSessionStartDate(session);
            const list = byDate.get(date) ?? [];
            list.push(session);
            byDate.set(date, list);
        });

    return buildRecentDayKeys(dayCount, now).map((date) => {
        const daySessions = (byDate.get(date) ?? []).sort(
            (a, b) => new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime(),
        );
        const chartSessions: FastingChartSession[] = daySessions.map((session, index) => {
            const durationMinutes = session.status === 'active' ? getElapsedMinutes(session, nowMs) : (session.durationMinutes ?? 0);
            return {
                id: session.id,
                durationMinutes,
                colorIndex: index % FASTING_PERIOD_COLORS.length,
            };
        });
        const totalMinutes = chartSessions.reduce((sum, s) => sum + s.durationMinutes, 0);
        return { date, sessions: chartSessions, totalMinutes };
    });
}

export function getFastingChartMaxMinutes(chartDays: readonly FastingChartDay[]): number {
    const maxDayMinutes = chartDays.reduce(
        (max, day) => Math.max(max, day.totalMinutes),
        0,
    );
    // Use 12 hours (720 minutes) as the baseline scale, so tiny fasts don't visually fill the chart
    return Math.max(720, maxDayMinutes);
}

export async function createCompletedFastingSession(
    userId: string,
    startedAt: string,
    endedAt: string,
    goalHours?: number,
): Promise<void> {
    const startedAtTime = new Date(startedAt).getTime();
    const endedAtTime = new Date(endedAt).getTime();
    if (Number.isNaN(startedAtTime) || Number.isNaN(endedAtTime) || endedAtTime <= startedAtTime) {
        throw new Error('Invalid fasting session range');
    }
    const durationMinutes = Math.max(1, Math.round((endedAtTime - startedAtTime) / 60000));
    await addDoc(sessionsCollection(userId), {
        startedAt,
        startDate: toLocalDateKey(startedAt),
        endedAt,
        durationMinutes,
        status: 'completed',
        ...(goalHours != null ? { goalHours } : {}),
    } satisfies Omit<FastingSession, 'id'>);
}

export async function deleteFastingSession(userId: string, sessionId: string): Promise<void> {
    await deleteDoc(doc(db, 'users', userId, 'fastingSessions', sessionId));
}

export async function updateFastingSession(
    userId: string,
    sessionId: string,
    updates: { startedAt: string; endedAt: string },
): Promise<void> {
    const startedAtTime = new Date(updates.startedAt).getTime();
    const endedAtTime = new Date(updates.endedAt).getTime();
    const durationMs = endedAtTime - startedAtTime;
    const durationMinutes = Math.max(1, Math.round(durationMs / 60000));
    
    await updateDoc(doc(db, 'users', userId, 'fastingSessions', sessionId), {
        startedAt: updates.startedAt,
        startDate: toLocalDateKey(updates.startedAt),
        endedAt: updates.endedAt,
        durationMinutes,
    });
}

export async function correctLongFastingSessions(userId: string, sessions: FastingSession[]): Promise<void> {
    const completed = sessions.filter(s => s.status === 'completed' && s.endedAt);
    
    for (const session of completed) {
        if (!session.endedAt) continue;
        
        const start = new Date(session.startedAt);
        const end = new Date(session.endedAt);
        const durationMs = end.getTime() - start.getTime();
        
        if (durationMs > 24 * 60 * 60 * 1000 + 60000) {
            const nextMidnight = new Date(start);
            nextMidnight.setHours(24, 0, 0, 0);
            
            if (end.getTime() > nextMidnight.getTime()) {
                const seg1Duration = nextMidnight.getTime() - start.getTime();
                await updateDoc(doc(db, 'users', userId, 'fastingSessions', session.id), {
                    endedAt: nextMidnight.toISOString(),
                    durationMinutes: Math.max(1, Math.round(seg1Duration / 60000)),
                });
                
                const seg2Duration = end.getTime() - nextMidnight.getTime();
                await addDoc(collection(db, 'users', userId, 'fastingSessions'), {
                    startedAt: nextMidnight.toISOString(),
                    startDate: toLocalDateKey(nextMidnight.toISOString()),
                    endedAt: session.endedAt,
                    durationMinutes: Math.max(1, Math.round(seg2Duration / 60000)),
                    status: 'completed',
                    goalHours: session.goalHours ?? 16,
                });
                
                break;
            }
        }
    }
}
